import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid'; // [FLUID-3 2026-10-12]
import { buttonPrimary, buttonSecondary, buttonDanger } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { lgSurface, lgSquircle, lgButtonPrimary, lgButtonSecondary, lgButtonDanger, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]

interface Props {
  open: boolean;
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  icon?: string; // font-awesome class, e.g. "fa-trash-alt"
  /** Alert mode: hide the cancel button so only a single acknowledge button shows. */
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** [LIQUID-GLASS 2026-11-02] true = the app's frosted glass window and glass buttons (Refunds / Disputes). Default: as before. */
  liquid?: boolean;
}

const Overlay = styled.div<{ $open: boolean; $liquid?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(20, 30, 25, 0.18)'};
  backdrop-filter: ${(p) => (p.$liquid ? 'blur(12px) saturate(115%)' : 'blur(14px) saturate(140%)')}; /* [LIQUID-GLASS] Edit Mapping's shade */
  -webkit-backdrop-filter: ${(p) => (p.$liquid ? 'blur(12px) saturate(115%)' : 'blur(14px) saturate(140%)')};
  /* [FLUID-3 2026-10-12] The shared shade recipe (components/Fluid.tsx): fades in 0.22 s, out 0.18 s, and takes
     no clicks while closed or closing. Was a 180 ms opacity transition. */
  ${(p) => fluidScrim(p.$open)}
`;

const Modal = styled.div<{ $open: boolean; $liquid?: boolean }>`
  position: relative;
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(230, 233, 231, 0.68) 100%)'};
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.65)'};
  color: ${(p) => p.theme.text};
  border-radius: 20px;
  isolation: isolate;
  box-shadow:
    0 30px 80px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0, 0, 0, 0.22)'},
    0 8px 24px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0, 0, 0, 0.12)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)'};
  width: 440px;
  max-width: calc(92vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  padding: 28px 28px 22px;
  text-align: center;
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the icon, title, message and buttons rise in one after another. Was a 0.97 scale
     settle on transition.modal. */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 4) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.12) 100%);
    pointer-events: none;
    z-index: 0;
  }

  & > * { position: relative; z-index: 1; }

  /* [LIQUID-GLASS 2026-11-02, user] liquid: the app's frosted glass (styles/liquidGlass.ts lgSurface — a fine grain, a light top
     edge, a soft bottom reflection; its ::before is the edge line), smooth corners */
  ${(p) => p.$liquid && css`
    border: none;
    ${lgSurface(36)}
    ${lgSquircle(22)}
  `}
`;

const IconCircle = styled.div<{ $destructive: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  font-size: 18px;
  /* [GREEN-ON-DARK 2026-08-27] greenInk/greenTint, not green600: the icon has to be READ on the
     dark surface, where green600 sits near 2:1. See theme.ts. */
  color: ${(p) => (p.$destructive ? p.theme.danger : p.theme.greenInk)};
  background: ${(p) => (p.$destructive ? 'rgba(220, 38, 38, 0.12)' : p.theme.greenTint)};
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 17px;
  font-weight: 700;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  text-shadow: ${(p) => p.theme.colorScheme === 'dark' ? '0 1px 2px rgba(0,0,0,0.45)' : 'none'};
  letter-spacing: -0.01em;
`;

const Message = styled.div`
  font-size: 13.5px;
  line-height: 1.55;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.78)' : p.theme.muted};
  margin: 0 auto 22px;
  max-width: 360px;

  strong {
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
    font-weight: 700;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

// [BUTTON-STYLE 2026-09-30] primary / danger / secondary = the three styles in styles/buttons;
// min-width stays (layout).
// [LIQUID-GLASS 2026-11-02] liquid: the glass capsules (a squish and a glow where pressed)
const Button = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $variant?: 'primary' | 'danger' | 'secondary'; $liquid?: boolean }>`
  ${(p) => (p.$liquid
    ? (p.$variant === 'danger' ? lgButtonDanger : p.$variant === 'secondary' ? lgButtonSecondary : lgButtonPrimary)
    : (p.$variant === 'danger' ? buttonDanger : p.$variant === 'secondary' ? buttonSecondary : buttonPrimary))}
  min-width: 96px;
`;

const ConfirmDialog: React.FC<Props> = ({
  open,
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  icon,
  hideCancel = false,
  onConfirm,
  onCancel,
  liquid = false,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
      if (e.key === 'Enter' && !busy) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel, onConfirm]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !busy) onCancel();
  };

  // [FLUID-3 2026-10-12] Shown one frame after `open` turns true, so the spring always has the closed state to
  // start from (a request that mounts this dialog and opens it in the same tick would otherwise skip the motion).
  const pres = usePresence(open, fluid.dialogOutMs);
  const shown = open && pres.entered;
  // [FLUID-3 2026-10-12] While it closes, the box keeps the words of its last open frame: callers clear the record
  // the message names (the row being deleted) at the moment of closing, which blanked the text before the fade.
  const view = { title, message, confirmLabel, cancelLabel, destructive, busy, icon, hideCancel };
  const lastView = useRef(view);
  if (open) lastView.current = view;
  const v = open ? view : lastView.current;

  const iconClass = v.icon ?? (v.destructive ? 'fa-trash-alt' : 'fa-check-circle');

  // Portal to <body> so the fixed overlay anchors to the viewport. Rendered
  // inline, a page container's backdrop-filter would become the containing
  // block for position:fixed and centre the dialog in the middle of the full
  // scrollable page instead of the visible area.
  // [AUTO-REFRESH 2026-08-03] data-reload-block is set only while OPEN — this overlay stays
  // mounted and merely toggles $open, so an unconditional marker would permanently block the
  // auto-reload (see services/reloadGuard).
  // [FLUID-3 2026-10-12] Inert whenever it is not open (closing, or closed and invisible): its buttons can't be
  // reached by Tab or used by a key press then — aria-hidden already says the same to screen readers.
  return createPortal(
    <Overlay
      $open={shown}
      $liquid={liquid}
      data-reload-block={open ? '' : undefined}
      onClick={handleOverlayClick}
      aria-hidden={!open}
      inert={!open}
    >
      <Modal $open={shown} $liquid={liquid} role="dialog" aria-modal="true">
        <IconCircle $destructive={v.destructive}>
          <i className={`fas ${iconClass}`}></i>
        </IconCircle>
        <Title>{v.title}</Title>
        <Message>{v.message}</Message>
        <Actions>
          {!v.hideCancel && (
            <Button $variant="secondary" $liquid={liquid} onClick={onCancel} disabled={v.busy}>
              {v.cancelLabel}
            </Button>
          )}
          <Button
            $variant={v.destructive ? 'danger' : 'primary'}
            $liquid={liquid}
            onClick={onConfirm}
            disabled={v.busy}
          >
            {v.busy ? 'Working…' : v.confirmLabel}
          </Button>
        </Actions>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default ConfirmDialog;
